import { IconPaperclip } from "@tabler/icons-react";
import { useText } from "../../clients/words";
import type { GolosLenty, VlozhenieLenty } from "../api";

/**
 * Вложение строки ленты — одно на ленту обращения и на хронику (`s619`).
 *
 * Вынесено из `Razbor.tsx` без правки вида: хроника клиентки теперь живёт на
 * трёх экранах, и вложение в ней обязано выглядеть так же, как в переписке.
 */
export function Vlozhenie({
  v,
  golos,
  onRasshifrovat,
  zanyat,
}: {
  v: VlozhenieLenty;
  golos: GolosLenty | null;
  onRasshifrovat: () => void;
  zanyat: boolean;
}) {
  const text = useText();

  if (!v.est_u_nas || v.adres === null) {
    /*
     * ⛔ `s619`: файла нет — строка называет ПРИЧИНУ словом сервера
     * («файл не сохранился у amo», «файл утерян», «забираем»), а не общее
     * «не сохранён» на всё подряд.
     */
    return (
      <span className="mb-1 block text-ops-micro text-ops-text-tertiary">
        {v.vid_display} — {v.sostoyanie_display ?? text("obrashcheniya.fayl_ne_sohranyon")}
      </span>
    );
  }

  /*
   * ⛔ АДРЕС ПРИХОДИТ ГОТОВЫМ И ПОДПИСАННЫМ, склеивать его здесь нечего.
   * Первая редакция собирала путь сама — и картинка не открылась ни разу:
   * `<img src>` идёт на другой домен без входа и без `Origin`, а Sanctum такой
   * запрос своим не считает. Поймал глаз, а не ловушка.
   */
  const adres = v.adres;

  /*
   * ⛔ ГОЛОСОВОЕ — ПЛЕЕР И РАСШИФРОВКА ПОД НИМ (владелец, п.8: «нет расшифровки
   * если надиктовали»). Плеер стоит всегда: текст машины — это пересказ, а не
   * запись, и последнее слово за ухом дежурного.
   *
   * ⛔ РАСПОЗНАВАТЕЛЬ СВОЙ, НА НАШЕМ СЕРВЕРЕ, БЕСПЛАТНЫЙ. Задание закладывало
   * платного «около рубля за минуту»; замер нашёл живой `gigaam-asr` на самом
   * сервере. Поэтому кнопка не выключена «до слова владельца» — платить не за
   * что; «всем автоматически» включается настройкой одним словом.
   */
  if (v.vid === "voice" || v.vid === "audio") {
    return (
      <span className="mb-1 block">
        {/*
          ⛔⛔ ШИРИНА ПЛЕЕРА — ЧИСЛОМ, А НЕ «ВО ВСЮ ШИРИНУ» (`s606`, поймано
          ЗАМЕРОМ на живом экране по слову владельца «голосовое — плеера нет»).

          Пузырь ленты сжимается по содержимому, а `w-full` — это «сто
          процентов родителя». У сжимающегося родителя ширина берётся из
          содержимого, содержимое просит сто процентов родителя — круг
          замыкается на нуле, и браузер рисует САМЫЙ МАЛЕНЬКИЙ вид кнопок:
          замер на живом экране дал 85 точек вместо 288. Выглядит это не как
          узкий плеер, а как мёртвая серая заглушка — ровно то, что владелец
          назвал «плеера нет».

          ⛔ `preload="metadata"`, А НЕ `none`: без первых байтов браузеру
          неоткуда взять длительность, и ползунок остаётся пустым даже у
          исправного файла. Метаданные — это сотни байт, не весь звук.
        */}
        <audio
          src={adres}
          controls
          preload="metadata"
          className="w-[14rem] max-w-full"
        />
        {golos?.tekst != null && (
          <span className="mt-1 block whitespace-pre-wrap break-words text-ops-micro text-ops-text-secondary">
            {golos.tekst}
          </span>
        )}
        {golos?.mozhno === true && (
          <button
            type="button"
            onClick={onRasshifrovat}
            disabled={zanyat}
            className="mt-1 text-ops-micro text-ops-primary underline underline-offset-2 disabled:opacity-40"
          >
            {golos.sostoyanie === "idyot"
              ? text("obrashcheniya.rasshifrovyvaem")
              : text("obrashcheniya.rasshifrovat")}
          </button>
        )}
        {golos?.mozhno === false && golos.tekst === null && (
          <span className="mt-1 block text-ops-micro text-ops-text-tertiary">
            {golos.sostoyanie === "ne_udalos"
              ? text("obrashcheniya.rasshifrovka_ne_udalas")
              : text("obrashcheniya.rasshifrovka_vyklyuchena")}
          </span>
        )}
      </span>
    );
  }

  if (v.vid === "image") {
    /*
     * ⛔⛔ У МЕСТА ПОД КАРТИНКУ ВЫСОТА ИЗВЕСТНА ДО ЗАГРУЗКИ (`s605`, поймано
     * ГЛАЗОМ на живом экране).
     *
     * Прежняя редакция задавала только потолок (`max-h-64`): пока фотография
     * едет, место под неё почти нулевое, а как приехала — лента подрастает на
     * несколько сотен точек. Два следствия, и оба владелец назвал своими
     * словами. Первое: открытый чат оказывался НЕ внизу — прокрутка успевала
     * встать в конец до загрузки, а потом конец уезжал (замер после правки:
     * запас 166 точек). Второе: этот же скачок раскладки и есть часть
     * «мерцания» на телефоне.
     *
     * `loading="lazy"` снят намеренно: в ленте, которую открывают снизу,
     * отложенная загрузка гарантирует скачок ровно там, где человек смотрит.
     */
    return (
      <a href={adres} target="_blank" rel="noreferrer" className="mb-1 block">
        {/*
          ⛔ ВЫСОТА ИЗВЕСТНА ЗАРАНЕЕ, НО КАРТИНКА НЕ ОБРЕЗАЕТСЯ. Первая редакция
          этой правки дала квадрат с `object-cover` — и поймал глаз: проба
          вложения (широкая полоса с текстом) превратилась в пустой белый
          квадрат, потому что в кадр попал её пустой угол. Скачок раскладки
          лечился ценой содержимого.

          Держим ПОЛОСУ известной высоты во всю ширину пузыря: высота не
          зависит от того, что приедет, а `object-contain` показывает
          фотографию целиком — и широкую, и высокую.
        */}
        <span className="block h-48 w-full overflow-hidden rounded-ops-md bg-ops-surface-2">
          <img
            src={adres}
            alt={v.vid_display}
            className="h-full w-full object-contain"
          />
        </span>
      </a>
    );
  }

  return (
    <a
      href={adres}
      target="_blank"
      rel="noreferrer"
      className="mb-1 flex items-center gap-1.5 text-ops-caption text-ops-primary underline underline-offset-2"
    >
      <IconPaperclip size={14} />
      {v.vid_display}
      {v.bayt !== null && (
        <span className="text-ops-text-tertiary">· {vesFayla(v.bayt)}</span>
      )}
    </a>
  );
}

/** Вес файла словами: килобайты до мегабайта, дальше — мегабайты. */
function vesFayla(bayt: number): string {
  return bayt < 1024 * 1024
    ? `${Math.max(1, Math.round(bayt / 1024))} КБ`
    : `${(bayt / 1024 / 1024).toFixed(1)} МБ`;
}

